import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

export default function GlobePage() {
  const globeEl = useRef(null);
  const globeRef = useRef(null);
  const controlsRef = useRef(null);
  const initialCameraState = useRef(null); // Store initial camera state
  const [currentZoomKm, setCurrentZoomKm] = useState(0);
  const [currentLayer, setCurrentLayer] = useState('Default');
  const [showCities, setShowCities] = useState(true);
  const keysPressed = useRef(new Set()); // Track pressed keys

  // Legend data matching the globe points
  const legendData = [
    { city: 'Brussels', color: '#ffffffff' },
    { city: 'New York', color: '#00ff3cff' },
    { city: 'Tokyo', color: '#ff0026ff' },
    { city: 'London', color: '#B76E79' },
    { city: 'Paris', color: '#ffa600ff',size:0.1 },
    { city: 'Jarnac', color: '#ff5733ff' },
    { city: 'Johannesburg', color: '#ff3300' },
    { city: 'Cairo', color: '#cc6600' },
    { city: 'Lagos', color: '#00cc66' },
    { city: 'Nairobi', color: '#339966' },
  ];

  const globeData = [
    { lat: 50.8503, lng: 4.3517, size: 0.05, color: '#ffffffff' }, // Brussels
    { lat: 40.7128, lng: -74.006, size: 0.05, color: '#00ff3cff' }, // NYC
    { lat: 35.6762, lng: 139.6503, size: 0.05, color: '#ff0026ff' }, // Tokyo
    { lat: 51.5074, lng: -0.1278, size: 0.05, color: '#B76E79' }, // London
    { lat: 48.8566, lng: 2.3522, size: 0.05, color: '#ffa600ff' }, // Paris
    { lat: 45.6843, lng: -0.1636, size: 0.05, color: '#ff5733ff' }, // Jarnac
    { lat: -26.2041, lng: 28.0473, size: 0.05, color: '#ff3300' }, // Johannesburg
    { lat: 30.0444, lng: 31.2357, size: 0.05, color: '#cc6600' }, // Cairo
    { lat: 6.5244, lng: 3.3792, size: 0.05, color: '#00cc66' }, // Lagos
    { lat: -1.2921, lng: 36.8219, size: 0.05, color: '#339966' }, // Nairobi
  ];

  const labelsData = globeData.map((point, index) => ({
    ...point,
    text: legendData[index].city,
    size: 0.005 // Higher altitude for labels above points
  }));

  // Topography layers
  const layers = [
    { 
      name: 'Default', 
      globeUrl: '//cdn.jsdelivr.net/npm/three-globe/example/img/earth-blue-marble.jpg',
      bumpUrl: '//cdn.jsdelivr.net/npm/three-globe/example/img/earth-topology.png' 
    },
    { 
      name: 'Blue Marble', 
      globeUrl: '//cdn.jsdelivr.net/npm/three-globe/example/img/earth-blue-marble.jpg',
      bumpUrl: '//cdn.jsdelivr.net/npm/three-globe/example/img/earth-topology.png' 
    },
    { 
      name: 'Night', 
      globeUrl: '//cdn.jsdelivr.net/npm/three-globe/example/img/earth-night.jpg',
      bumpUrl: '//cdn.jsdelivr.net/npm/three-globe/example/img/earth-topology.png' 
    },
    { 
      name: 'Day', 
      globeUrl: '//cdn.jsdelivr.net/npm/three-globe/example/img/earth-day.jpg',
      bumpUrl: '//cdn.jsdelivr.net/npm/three-globe/example/img/earth-topology.png' 
    },
  ];

  const resetGlobe = () => {
    if (controlsRef.current && initialCameraState.current && globeRef.current) {
      // Restore initial camera position and controls state
      globeRef.current.camera().position.copy(initialCameraState.current.position);
      controlsRef.current.target.copy(initialCameraState.current.target);
      controlsRef.current.zoom = initialCameraState.current.zoom;
      controlsRef.current.update();
      // Update zoom distance after reset
      setTimeout(calculateZoomDistance, 100);
    }
  };

  const changeLayer = (layerName) => {
    const layerData = layers.find(layer => layer.name === layerName);
    if (layerData && globeRef.current) {
      globeRef.current.globeImageUrl(layerData.globeUrl);
      globeRef.current.bumpImageUrl(layerData.bumpUrl);
      setCurrentLayer(layerName);
    }
  };

  const calculateZoomDistance = () => {
    if (globeRef.current && controlsRef.current) {
      const camera = globeRef.current.camera();
      const controls = controlsRef.current;
      
      // Get the distance from controls (more reliable)
      const distanceToCenter = controls.object ? controls.object.position.distanceTo(controls.target) : camera.position.length();
      
      // Globe radius in three-globe is 100 units
      const globeRadius = 100;
      
      // Calculate height above surface (use absolute distance)
      const heightAboveSurface = Math.max(0, distanceToCenter - globeRadius);
      
      // Convert to real Earth kilometers (Earth radius = 6371 km, globe radius = 100 units)
      const earthRadiusKm = 6371;
      const scaleFactor = earthRadiusKm / globeRadius;
      const heightKm = heightAboveSurface * scaleFactor;
      
      // Ensure it's not negative and round appropriately
      const finalHeight = Math.max(0, Math.round(heightKm));
      setCurrentZoomKm(finalHeight);
    }
  };

  // Keyboard control functions
  const panCamera = (direction, distance = 10) => {
    if (!controlsRef.current) return;
    
    const controls = controlsRef.current;
    const camera = globeRef.current.camera();
    
    // Get the camera's up and right vectors
    const up = new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(camera.position, up).normalize();
    const forward = new THREE.Vector3().subVectors(controls.target, camera.position).normalize();
    
    let panVector = new THREE.Vector3();
    
    switch (direction) {
      case 'up':
        panVector = up.clone().multiplyScalar(distance);
        break;
      case 'down':
        panVector = up.clone().multiplyScalar(-distance);
        break;
      case 'left':
        panVector = right.clone().multiplyScalar(-distance);
        break;
      case 'right':
        panVector = right.clone().multiplyScalar(distance);
        break;
    }
    
    // Apply the pan
    controls.target.add(panVector);
    camera.position.add(panVector);
    controls.update();
    
    // Update zoom distance
    calculateZoomDistance();
  };

  const handleKeyDown = (event) => {
    const key = event.key.toLowerCase();
    if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'w', 'a', 's', 'd'].includes(key)) {
      event.preventDefault();
      keysPressed.current.add(key);
      
      // Map keys to directions
      const keyMap = {
        'arrowup': 'up', 'w': 'up',
        'arrowdown': 'down', 's': 'down', 
        'arrowleft': 'left', 'a': 'left',
        'arrowright': 'right', 'd': 'right'
      };
      
      if (keyMap[key]) {
        panCamera(keyMap[key], 10); // Smaller distance for smoother control
      }
    }
  };

  const handleKeyUp = (event) => {
    const key = event.key.toLowerCase();
    keysPressed.current.delete(key);
  };

  useEffect(() => {
    if (!globeEl.current) return;

    const initGlobe = async () => {
      const { default: Globe } = await import('globe.gl');

      const globe = new Globe(globeEl.current)
        .width(window.innerWidth)
        .height(window.innerHeight)
        .backgroundColor('rgba(0,0,0,0)')
        .globeImageUrl(layers[0].globeUrl)
        .showGlobe(true)
        .bumpImageUrl(layers[0].bumpUrl)
        .backgroundImageUrl(`${process.env.PUBLIC_URL}/night-sky.png`)
        .showAtmosphere(true)
        .atmosphereColor('#289248')
        .pointsData(showCities ? globeData : [])
        .pointLat('lat')
        .pointLng('lng')
        .pointColor('color')
        .pointAltitude('size')
        .pointRadius(0.25)
        .onPointClick((point) => alert(`Clicked: ${point.lat}, ${point.lng}`))
        .labelsData(showCities ? labelsData : [])
        .labelLat('lat')
        .labelLng('lng')
        .labelText('text')
        .labelSize(0.8)
        .labelColor('color')
        .labelAltitude(0.05);

      globeRef.current = globe; // Store globe instance

      // Enable manual controls using globe's built-in controls
      const controls = globe.controls();
      controls.autoRotate = false;
      controls.autoRotateSpeed = 0;
      controls.enableZoom = true;
      controls.enablePan = true;
      controls.enableRotate = true;
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.minDistance = 150;
      controls.maxDistance = 800;
      controls.panSpeed = 1.0;
      controls.rotateSpeed = 1.0;
      controls.zoomSpeed = 1.0;

      controlsRef.current = controls; // Store controls reference

      // Store initial camera state for reset functionality
      initialCameraState.current = {
        position: globe.camera().position.clone(),
        target: controls.target.clone(),
        zoom: controls.zoom
      };

      // Set up zoom distance calculation
      calculateZoomDistance();
      
      // Update zoom distance on user interactions
      let updateTimeout;
      const throttledUpdate = () => {
        clearTimeout(updateTimeout);
        updateTimeout = setTimeout(calculateZoomDistance, 100);
      };
      
      // Listen to mouse and wheel events
      window.addEventListener('wheel', throttledUpdate);
      window.addEventListener('mousedown', throttledUpdate);
      window.addEventListener('mouseup', throttledUpdate);
      
      // Add keyboard controls
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      
      // Prevent right-click context menu to allow panning
      const preventContextMenu = (e) => e.preventDefault();
      globeEl.current.addEventListener('contextmenu', preventContextMenu);

      const handleResize = () => {
        globe.width(window.innerWidth).height(window.innerHeight);
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('wheel', throttledUpdate);
        window.removeEventListener('mousedown', throttledUpdate);
        window.removeEventListener('mouseup', throttledUpdate);
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
        globeEl.current.removeEventListener('contextmenu', preventContextMenu);
        window.removeEventListener('resize', handleResize);
        clearTimeout(updateTimeout);
        if (globe.renderer()) {
          globe.renderer().dispose();
        }
      };
    };

    initGlobe();
  }, []);

  // Effect to toggle cities visibility
  useEffect(() => {
    if (globeRef.current) {
      globeRef.current.pointsData(showCities ? globeData : []);
      globeRef.current.labelsData(showCities ? labelsData : []);
    }
  }, [showCities]);

  return (
    <>
      {/* Globe Container */}
      <div
        ref={globeEl}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1,
          backgroundColor: 'white',
        }}
      />

      {/* Control Instructions */}
      <div
        style={{
          position: 'fixed',
          top: '100px',
          right: '20px',
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          padding: '10px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          fontSize: '12px',
          fontFamily: 'Arial, sans-serif',
          maxWidth: '200px',
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Globe Controls</div>
        <div style={{ marginBottom: '4px' }}>🖱️ <strong>Rotate:</strong> Click and drag</div>
        <div style={{ marginBottom: '4px' }}>🔍 <strong>Zoom:</strong> Scroll wheel</div>
        <div style={{ marginBottom: '4px' }}>📍 <strong>Pan:</strong> Right-click and drag</div>
        <div style={{ marginBottom: '4px' }}>⌨️ <strong>Keyboard:</strong> Arrow keys or WASD</div>
        <div style={{ marginBottom: '8px', fontSize: '14px', color: '#000000ff' }}>
          Full 3D navigation enabled!
        </div>
        <button
          onClick={resetGlobe}
          style={{
            marginTop: '8px',
            padding: '4px 8px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
          }}
        >
          Reset View
        </button>
      </div>

      <div
        className="cities-legend"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Cities</div>
        {legendData.map((item, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: item.color,
                borderRadius: '50%',
                marginRight: '8px',
              }}
            />
            <span>{item.city}</span>
          </div>
        ))}
        <button
          onClick={() => setShowCities(!showCities)}
          style={{
            marginTop: '8px',
            padding: '4px 8px',
            backgroundColor: showCities ? '#28a745' : '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          {showCities ? 'Hide Cities' : 'Show Cities'}
        </button>
      </div>

      {/* Layers Table in Bottom Left */}
      <div
        className="layers-table"
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Topography Layers</div>
        {layers.map((layer, idx) => (
          <div
            key={idx}
            onClick={() => changeLayer(layer.name)}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '4px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              backgroundColor: currentLayer === layer.name ? 'rgba(0,123,255,0.1)' : 'transparent',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,123,255,0.05)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = currentLayer === layer.name ? 'rgba(0,123,255,0.1)' : 'transparent'}
          >
            <span>{layer.name}</span>
          </div>
        ))}
      </div>

      {/* Zoom Distance Indicator */}
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          padding: '8px 16px',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'center',
        }}
      >
        {currentZoomKm.toLocaleString()} km above surface
      </div>
    </>
  );
}